import React, { useState } from 'react';
import { User, LogOut, ChevronDown, UserCircle, Shield, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import BookingTracker from './BookingTracker';

const API = '/api';

const UserMenu = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/auth/logout`, null, {
        params: { token }
      });
      
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      toast.success('Logged out successfully');
      onLogout();
      setIsOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API fails, clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      onLogout();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-full px-4 py-2 transition-all"
      >
        <UserCircle className="text-purple-400" size={20} />
        <span className="text-white text-sm font-medium hidden sm:inline">{user.name}</span>
        <ChevronDown className="text-purple-400" size={16} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-gray-900 border border-purple-500/30 rounded-xl shadow-xl z-20 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-purple-500/20">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-600/20 p-2 rounded-full">
                  <User className="text-purple-400" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{user.name}</p>
                  <p className="text-gray-400 text-sm truncate">{user.email}</p>
                </div>
              </div>
            </div>

            {/* Booking Tracker */}
            <div className="p-4 border-b border-purple-500/20">
              <BookingTracker userEmail={user.email} />
            </div>

            <div className="p-2">
              <button
                onClick={() => {
                  navigate('/profile');
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-left text-white hover:bg-purple-600/20 rounded-lg transition-colors mb-2"
              >
                <User size={18} />
                <span>My Profile</span>
              </button>
              <button
                data-testid="user-menu-artist-plan"
                onClick={() => {
                  navigate('/artist-plan');
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-left text-yellow-200 hover:bg-yellow-500/10 rounded-lg transition-colors mb-2"
              >
                <Star size={18} />
                <span>Artist Plan</span>
              </button>
              
              {user.is_admin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-purple-300 hover:bg-purple-600/20 rounded-lg transition-colors mb-2"
                >
                  <Shield size={18} />
                  <span>Admin Panel</span>
                </button>
              )}
              {user.is_employee && user.engineer_id && (
                <button
                  onClick={() => {
                    navigate('/employee');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-blue-300 hover:bg-blue-600/20 rounded-lg transition-colors mb-2"
                >
                  <UserCircle size={18} />
                  <span>Employee Dashboard</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-300 hover:bg-purple-600/20 rounded-lg transition-colors"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
